/**
 * Marketing, Advertising & Conversion Tracking Domain API Service
 */

import { api } from './client';
import { TikTokAdsConfig, AdSpendDailyLog, AdProfitabilityResponse } from '../../types';

export const marketingAPI = {
  // Integrations generic
  getIntegrations: async () => api.get('/admin/integrations/'),
  createIntegration: async (data: any) => api.post('/admin/integrations/', data),
  updateIntegration: async (id: number, data: any) => api.put(`/admin/integrations/${id}/`, data),
  deleteIntegration: async (id: number) => api.delete(`/admin/integrations/${id}/`),
  testIntegrationShippingConnection: async (id: number) => api.post(`/admin/integrations/${id}/test_connection/`),
  testIntegrationFraudConnection: async (id: number) => api.post(`/admin/integrations/${id}/test_fraud_connection/`),

  // Facebook Pixel
  getFacebookPixel: async () => api.get('/admin/facebook-pixel/'),
  createFacebookPixel: async (data: any) => api.post('/admin/facebook-pixel/', data),
  updateFacebookPixel: async (id: number, data: any) => api.put(`/admin/facebook-pixel/${id}/`, data),
  deleteFacebookPixel: async (id: number) => api.delete(`/admin/facebook-pixel/${id}/`),

  // Facebook CAPI
  getFacebookCAPI: async () => api.get('/admin/facebook-capi/'),
  createFacebookCAPI: async (data: any) => api.post('/admin/facebook-capi/', data),
  updateFacebookCAPI: async (id: number, data: any) => api.put(`/admin/facebook-capi/${id}/`, data),
  deleteFacebookCAPI: async (id: number) => api.delete(`/admin/facebook-capi/${id}/`),
  testFacebookCAPI: async (id: number) => api.post(`/admin/facebook-capi/${id}/test_connection/`),

  // Google Tag Manager
  getGTM: async () => api.get('/admin/google-tag-manager/'),
  createGTM: async (data: any) => api.post('/admin/google-tag-manager/', data),
  updateGTM: async (id: number, data: any) => api.put(`/admin/google-tag-manager/${id}/`, data),
  deleteGTM: async (id: number) => api.delete(`/admin/google-tag-manager/${id}/`),

  // Meta Ads Configuration
  getMetaAdsConfig: async () => api.get('/admin/meta-ads-config/'),
  createMetaAdsConfig: async (data: any) => api.post('/admin/meta-ads-config/', data),
  updateMetaAdsConfig: async (id: number, data: any) => api.put(`/admin/meta-ads-config/${id}/`, data),
  deleteMetaAdsConfig: async (id: number) => api.delete(`/admin/meta-ads-config/${id}/`),
  testMetaAdsConnection: async (id: number) => api.post(`/admin/meta-ads-config/${id}/test_connection/`),
  fetchMetaPages: async (data?: { access_token?: string; config_id?: number }) =>
    api.post('/admin/meta-ads-config/fetch_pages/', data || {}),

  // Meta Ads Campaigns & Insights
  getMetaAdsCampaigns: async (datePreset: string = 'last_7d') => api.get(`/admin/meta-ads/campaigns/?date_preset=${datePreset}`),
  getMetaAdsCampaignInsights: async (campaignId: string, datePreset: string = 'last_7d') => api.get(`/admin/meta-ads/campaigns/${campaignId}/insights/?date_preset=${datePreset}`),

  // Campaign Products (product-to-campaign links)
  getCampaignProducts: async () => api.get('/admin/campaign-products/'),
  createCampaignProduct: async (data: any) => api.post('/admin/campaign-products/', data),
  deleteCampaignProduct: async (id: number) => api.delete(`/admin/campaign-products/${id}/`),
  searchCampaignProducts: async (query: string) => api.get(`/admin/campaign-products/search_products/?q=${encodeURIComponent(query)}`),

  // TikTok Pixel
  getTikTokPixel: async () => api.get('/admin/tiktok-pixel/'),
  createTikTokPixel: async (data: any) => api.post('/admin/tiktok-pixel/', data),
  updateTikTokPixel: async (id: number, data: any) => api.put(`/admin/tiktok-pixel/${id}/`, data),
  deleteTikTokPixel: async (id: number) => api.delete(`/admin/tiktok-pixel/${id}/`),

  // TikTok Events API
  getTikTokEventsAPI: async () => api.get('/admin/tiktok-events-api/'),
  createTikTokEventsAPI: async (data: any) => api.post('/admin/tiktok-events-api/', data),
  updateTikTokEventsAPI: async (id: number, data: any) => api.put(`/admin/tiktok-events-api/${id}/`, data),
  deleteTikTokEventsAPI: async (id: number) => api.delete(`/admin/tiktok-events-api/${id}/`),
  testTikTokEventsAPI: async (id: number) => api.post(`/admin/tiktok-events-api/${id}/test_connection/`),

  // TikTok Marketing API (Ads Account) Configuration
  getTikTokAdsConfig: async () => api.get<TikTokAdsConfig[]>('/admin/tiktok-ads-config/'),
  createTikTokAdsConfig: async (data: Partial<TikTokAdsConfig>) => api.post<TikTokAdsConfig>('/admin/tiktok-ads-config/', data),
  updateTikTokAdsConfig: async (id: number, data: Partial<TikTokAdsConfig>) => api.put<TikTokAdsConfig>(`/admin/tiktok-ads-config/${id}/`, data),
  deleteTikTokAdsConfig: async (id: number) => api.delete(`/admin/tiktok-ads-config/${id}/`),
  testTikTokAdsConnection: async (id: number) => api.post(`/admin/tiktok-ads-config/${id}/test_connection/`),
  syncTikTokAds: async (id: number, dateRange?: { start_date?: string; end_date?: string }) =>
    api.post(`/admin/tiktok-ads-config/${id}/sync_now/`, dateRange || {}),

  // Ad Spend Daily Logs
  getAdSpendLogs: async (params?: any) => api.get<AdSpendDailyLog[]>('/admin/ad-spend-logs/', { params }),
  createAdSpendLog: async (data: Partial<AdSpendDailyLog>) => api.post<AdSpendDailyLog>('/admin/ad-spend-logs/', data),
  updateAdSpendLog: async (id: number, data: Partial<AdSpendDailyLog>) => api.put<AdSpendDailyLog>(`/admin/ad-spend-logs/${id}/`, data),
  deleteAdSpendLog: async (id: number) => api.delete(`/admin/ad-spend-logs/${id}/`),
  bulkSaveAdSpendLogs: async (entries: Partial<AdSpendDailyLog>[]) => api.post('/admin/ad-spend-logs/bulk_save/', { entries }),
  fetchLiveAd: async (data: { platform: string; ad_id: string; date?: string; save_to_db?: boolean }) =>
    api.post('/admin/ad-spend-logs/fetch_live_ad/', data),
  syncPlatformAds: async (data: { platform: string; start_date?: string; end_date?: string; clean_other_pages?: boolean }) =>
    api.post('/admin/ad-spend-logs/sync_platform/', data),
  syncMetaAds: async (id: number, dateRange?: { start_date?: string; end_date?: string; clean_other_pages?: boolean }) =>
    api.post(`/admin/meta-ads-config/${id}/sync_now/`, dateRange || {}),

  // Profitability Analytics
  getAdProfitabilityAnalytics: async (params?: {
    platform?: string;
    date_preset?: string;
    start_date?: string;
    end_date?: string;
    ad_id?: string;
    page_id?: string;
  }) => api.get<AdProfitabilityResponse>('/admin/analytics/ad-profitability/', { params }),
};

export const publicAPI = {
  // Get active Facebook Pixel (no auth required)
  getActiveFacebookPixel: async () => {
    const response = await api.get('/integrations/facebook-pixel/active/');
    return response.data;
  },

  // Get active GTM configuration (no auth required)
  getActiveGTM: async () => {
    const response = await api.get('/integrations/gtm/active/');
    return response.data;
  },

  // Get active TikTok Pixel (no auth required)
  getActiveTikTokPixel: async () => {
    const response = await api.get('/integrations/tiktok-pixel/active/');
    return response.data;
  },
};
